import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import {
  Badge,
  Button,
  PageContainer,
  PageIntroCard,
  PageSectionCard,
  Skeleton,
  toast,
} from '@kmgestao/ui';

import { useCurrentClinicId } from '../../../features/auth/index.js';
import {
  formatBRL,
  formatDateBR,
  useOwnNextInvoice,
  useOwnSubscription,
  type OwnInvoiceRow,
  type OwnSubscriptionWithPlan,
} from '../../../features/clinic-billing/index.js';
import { supabase } from '../../../lib/supabase.js';
import { ClinicShell } from '../ClinicShell.js';

/**
 * Tela exibida quando a clínica do owner está em
 * `subscriptions.status='incomplete'` (Modelo C híbrido — ADR 0009).
 *
 * Owner ativou o convite (signup feito + senha definida em
 * `/welcome` — PR-M2-00), mas a primeira fatura no Asaas ainda não
 * foi confirmada. Acesso às rotas operacionais fica bloqueado pelo
 * `RequireClinicSubscriptionActive` até que o `asaas-webhook` receba
 * `PAYMENT_CONFIRMED` (transição `'incomplete' → 'active'`).
 *
 * ## Conteúdo (PR-M2-00, 2026-04-25)
 *
 * Antes (PR-M1-01h): texto genérico explicando o estado + botão
 * "Recarregar". Owner não via valor nem boleto/PIX — tinha que
 * procurar no email separado do Asaas.
 *
 * Agora:
 *   - **Plano contratado** (nome + valor + ciclo) — vem de
 *     `useOwnSubscription` + `plans` embedado.
 *   - **Próxima fatura** (valor + vencimento + link Asaas pra pagar)
 *     — vem de `useOwnNextInvoice` (status `pending`/`overdue`,
 *     ordem `due_date asc`).
 *   - **Polling 10s** na subscription — quando webhook ativar
 *     `'incomplete' → 'active'`, refresh JWT e auto-navigate pra
 *     `/dashboard` (sem owner precisar dar F5).
 *
 * ## Light + dark + mobile-first
 * Tokens DS, sem hardcode. Cards empilham em mobile (375×667).
 */
export function PaymentPendingPage() {
  const { t } = useT('clinics');
  const navigate = useNavigate();
  const clinicId = useCurrentClinicId();

  // Polling 10s — quando o asaas-webhook recebe PAYMENT_CONFIRMED, ele
  // muda subscription.status pra 'active'. Próximo poll detecta a
  // mudança e dispara o auto-redirect via useEffect abaixo. 10s é
  // sweet spot: rápido o bastante pra owner que pagou via Pix sentir
  // "instantâneo", devagar o bastante pra não inflar requests.
  const subQ = useOwnSubscription(clinicId, { refetchInterval: 10_000 });
  const invoiceQ = useOwnNextInvoice(clinicId);

  const subscriptionStatus = subQ.data?.subscription.status ?? null;

  // Auto-redirect quando subscription.status sair de 'incomplete'.
  // Refresh JWT primeiro pra garantir que o claim
  // `current_clinic_subscription_status` no token também esteja
  // atualizado — sem isso, RequireClinicSubscriptionActive ainda
  // bloqueia /dashboard pelos próximos minutos (até refresh natural).
  useEffect(() => {
    if (!subscriptionStatus) return;
    if (subscriptionStatus === 'incomplete') return;
    void (async () => {
      try {
        await supabase.auth.refreshSession();
      } catch {
        // best-effort — se refresh falhar, owner faz F5 manual via botão
      }
      toast.success(t('paymentPending.releasedToast'));
      navigate('/dashboard', { replace: true });
    })();
  }, [subscriptionStatus, navigate, t]);

  return (
    <ClinicShell>
      <PageContainer>
        <PageIntroCard
          eyebrow="⏳"
          title={t('paymentPending.title')}
          description={t('paymentPending.description')}
        />

        {/* Card 1 — Plano contratado */}
        <PageSectionCard
          title={t('paymentPending.plan.title')}
          description={t('paymentPending.plan.description')}
        >
          {subQ.isLoading ? (
            <PlanSkeleton />
          ) : subQ.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {t('paymentPending.plan.loadError', {
                message: subQ.error instanceof Error ? subQ.error.message : '',
              })}
            </p>
          ) : subQ.data ? (
            <PlanInfo data={subQ.data} />
          ) : (
            <p className="text-sm text-muted-foreground">{t('paymentPending.plan.empty')}</p>
          )}
        </PageSectionCard>

        {/* Card 2 — Próxima fatura + link Asaas */}
        <PageSectionCard
          title={t('paymentPending.invoice.title')}
          description={t('paymentPending.invoice.description')}
        >
          {invoiceQ.isLoading ? (
            <InvoiceSkeleton />
          ) : invoiceQ.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {t('paymentPending.invoice.loadError', {
                message: invoiceQ.error instanceof Error ? invoiceQ.error.message : '',
              })}
            </p>
          ) : invoiceQ.data ? (
            <InvoiceInfo invoice={invoiceQ.data} />
          ) : (
            <p className="text-sm text-muted-foreground">{t('paymentPending.invoice.empty')}</p>
          )}
        </PageSectionCard>

        {/* Card 3 — Como funciona (FAQ) */}
        <PageSectionCard
          title={t('paymentPending.faq.title')}
          description={t('paymentPending.faq.description')}
        >
          <ul className="space-y-3 text-sm">
            <li className="flex gap-2">
              <span aria-hidden="true" className="text-accent-foreground">
                →
              </span>
              <span>
                <strong className="text-foreground">{t('paymentPending.faq.pixTitle')}:</strong>{' '}
                {t('paymentPending.faq.pixDescription')}
              </span>
            </li>
            <li className="flex gap-2">
              <span aria-hidden="true" className="text-accent-foreground">
                →
              </span>
              <span>
                <strong className="text-foreground">{t('paymentPending.faq.cardTitle')}:</strong>{' '}
                {t('paymentPending.faq.cardDescription')}
              </span>
            </li>
            <li className="flex gap-2">
              <span aria-hidden="true" className="text-accent-foreground">
                →
              </span>
              <span>
                <strong className="text-foreground">{t('paymentPending.faq.boletoTitle')}:</strong>{' '}
                {t('paymentPending.faq.boletoDescription')}
              </span>
            </li>
          </ul>
        </PageSectionCard>

        {/* Polling info + reload manual fallback */}
        <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          <p>
            <span aria-hidden="true">🔄</span> {t('paymentPending.polling.info')}
          </p>
          <p className="mt-2">
            {t('paymentPending.polling.fallback')}{' '}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="font-medium text-foreground underline-offset-4 hover:underline"
            >
              {t('paymentPending.actions.reload')}
            </button>
            .
          </p>
        </div>
      </PageContainer>
    </ClinicShell>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────

function PlanInfo({ data }: { data: OwnSubscriptionWithPlan }) {
  const { t } = useT('clinics');
  const { subscription, plan } = data;
  const cycleLabel =
    subscription.billing_cycle === 'yearly'
      ? t('paymentPending.plan.cycle.yearly')
      : t('paymentPending.plan.cycle.monthly');
  const price =
    subscription.billing_cycle === 'yearly' ? plan?.yearly_price_brl : plan?.monthly_price_brl;
  return (
    <dl className="grid gap-4 sm:grid-cols-2">
      <div>
        <dt className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('paymentPending.plan.fields.name')}
        </dt>
        <dd className="mt-1 text-base font-medium text-foreground">{plan?.name_pt_br ?? '—'}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('paymentPending.plan.fields.cycle')}
        </dt>
        <dd className="mt-1 text-base font-medium text-foreground">{cycleLabel}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('paymentPending.plan.fields.price')}
        </dt>
        <dd className="mt-1 text-base font-medium text-foreground">
          {price != null ? formatBRL(price) : '—'}
        </dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('paymentPending.plan.fields.status')}
        </dt>
        <dd className="mt-1">
          <Badge variant="outline">
            {t(`paymentPending.plan.statusLabels.${subscription.status}`)}
          </Badge>
        </dd>
      </div>
    </dl>
  );
}

function InvoiceInfo({ invoice }: { invoice: OwnInvoiceRow }) {
  const { t } = useT('clinics');
  const dueDate = formatDateBR(invoice.due_date);
  return (
    <div className="space-y-4">
      <dl className="grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">
            {t('paymentPending.invoice.fields.amount')}
          </dt>
          <dd className="mt-1 text-2xl font-semibold text-foreground">
            {formatBRL(invoice.amount_brl)}
          </dd>
        </div>
        <div>
          <dt className="text-xs uppercase tracking-wider text-muted-foreground">
            {t('paymentPending.invoice.fields.dueDate')}
          </dt>
          <dd className="mt-1 text-base font-medium text-foreground">{dueDate ?? '—'}</dd>
        </div>
      </dl>

      {invoice.invoice_url ? (
        <div className="flex flex-wrap gap-2">
          <Button asChild size="lg">
            <a href={invoice.invoice_url} target="_blank" rel="noopener noreferrer">
              {t('paymentPending.invoice.actions.pay')}
            </a>
          </Button>
          {invoice.pdf_url ? (
            <Button asChild size="lg" variant="outline">
              <a href={invoice.pdf_url} target="_blank" rel="noopener noreferrer">
                {t('paymentPending.invoice.actions.downloadPdf')}
              </a>
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('paymentPending.invoice.noLink')}{' '}
          <Link
            to="/dashboard"
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            {t('paymentPending.actions.dashboard')}
          </Link>
        </p>
      )}
    </div>
  );
}

function PlanSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-4 w-1/3" />
    </div>
  );
}

function InvoiceSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-7 w-32" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-11 w-40" />
    </div>
  );
}

// `formatBRL` e `formatDateBR` extraídos pra
// `features/clinic-billing/formatters.ts` em PR-M2-02 (PlanPage também
// usa). Deixar aqui causaria duplicação.
